import ExcelJS from 'exceljs';
import { LastCargoesReportData } from '../ReportGeneratorTypes';
import Decimal from 'decimal.js';
import { generateReportHeaderExcel } from './reportHeader';
import { getCoatingName } from 'src/utils/stowage/tankUtils';
import { getFormattedFillRatio, getLetterForNumber, getOrdinalSuffix, setCargoColor, setHeaderStyle } from '../excelReportUtils';
import { getCargoFosfaCellStatus, getFosfaAcceptedCargoIndex, MAX_FOSFA_CARGOES } from 'src/utils/stowage/tankPlanFilterUtils';
import sortUtils from 'src/utils/sortUtils';

const DATA_START_ROW = 7;

/**
 * Generates an Excel report showing the last cargoes for each tank
 * @param workbook - ExcelJS workbook instance
 * @param data - Report data containing tank cargo history information
 * @param logoImageId - ID of the logo image in the workbook
 * @param fosfaMode - Controls FOSFA-related filtering/coloring:
 *   - 'off' (default): no coloring, no filtering (plain Last Cargoes report).
 *   - 'approved': Last Cargo - Approved behavior. Only tanks with an acceptable cargo above 60%
 *     within the first three acceptable previous cargoes are included (unless includeAllTanks).
 *     Green is applied to the cargo that satisfies that rule.
 *   - 'nobl': Last Cargo - NOBL behavior. Tanks are excluded only when their immediate previous
 *     cargo is 'banned' (unless includeAllTanks) - 'acceptable' and 'none' are both kept. The
 *     immediate previous cargo column is colored green when 'acceptable', red when 'banned' (only
 *     reachable when includeAllTanks bypasses the filter), and left uncolored when 'none'. The
 *     2nd/3rd previous cargo columns behave the same as 'approved' (red when 'banned').
 * @param includeUnNo - When true, includes the UN No. column for each cargo group. Defaults to false.
 * @param includeAllTanks - When false (default) and fosfaMode is 'approved' or 'nobl', tanks are
 *   filtered per the rules above. When true, all tanks are included regardless of FOSFA status.
 *   Has no effect when fosfaMode is 'off'.
 * @param includeFillRatio - Only relevant when fosfaMode is 'off'. When true, includes the Fill
 *   ratio column. Defaults to false. Fill ratio is always included when fosfaMode is 'approved'
 *   or 'nobl', regardless of this flag.
 */
export function generateLastCargoesReportExcel(
    workbook: ExcelJS.Workbook,
    data: LastCargoesReportData,
    logoImageId: number,
    fosfaMode: 'off' | 'approved' | 'nobl' = 'off',
    includeUnNo = false,
    includeAllTanks = false,
    includeFillRatio = false
): void {
    const { numberOfSteps, selectedBerthCall, cargoNameType } = data;
    // Fill ratio is mandatory (always shown) for the FOSFA/NOBL variants; for the plain 3LC
    // report (fosfaMode 'off') it's only shown when explicitly requested via includeFillRatio.
    const showFillRatio = fosfaMode !== 'off' || includeFillRatio;
    const _columnsPerCargo = 1 + (includeUnNo ? 1 : 0) + (showFillRatio ? 1 : 0);

    const _worksheet = workbook.addWorksheet('Last Cargoes');

    let _title = `${numberOfSteps} Last Cargoes in ${data.selectedPortCall?.voyageNumber} ${data.selectedPortCall?.portDisplayName ?? ''}`;
    if (selectedBerthCall && selectedBerthCall.berthName) {
        _title += ` - ${selectedBerthCall.berthName}`;
    }

    // Column widths are set up-front (before generating the header) so the header can size
    // itself against the table's real widths - e.g. the Trade Name column can be widened well
    // beyond the default width, see below.
    _worksheet.getColumn(1).width = 10; // Tank
    _worksheet.getColumn(2).width = 10; // Coating
    _worksheet.getColumn(3).width = 10; // 98% Vol

    // When UN No. and/or Fill ratio columns are omitted, widen the Trade Name column to use the
    // freed space - otherwise long product names get truncated (no adjacent blank cell left for
    // Excel to overflow the text into).
    const tradeNameWidth = 30 + (includeUnNo ? 0 : 10) + (showFillRatio ? 0 : 10);
    for (let i = 0; i < numberOfSteps; i++) {
        _worksheet.getColumn(4 + i * _columnsPerCargo).width = tradeNameWidth; // Trade Name
        if (includeUnNo) {
            _worksheet.getColumn(5 + i * _columnsPerCargo).width = 10; // UN No.
        }
        if (showFillRatio) {
            _worksheet.getColumn(4 + i * _columnsPerCargo + (_columnsPerCargo - 1)).width = 10; // Fill ratio
        }
    }

    // Base columns (Tank, Coating, 98% Vol.) plus the cargo group columns - this is the table's
    // real, 1-indexed last column, so the header/title row (border line, charterers, "Created"
    // date) always spans exactly as wide as the table below it, regardless of how many optional
    // columns (UN No., Fill ratio) are included.
    const _tableLastColumn = 3 + numberOfSteps * _columnsPerCargo;

    // Size the vessel name/title box to the smallest number of (real, possibly widened) table
    // columns needed to reach a minimum readable width, instead of a fixed column count - a
    // handful of default-width columns can be much narrower than a single widened Trade Name
    // column, and vice versa. At least one column is always reserved after it for "Created".
    const DEFAULT_COLUMN_WIDTH = 8.43;
    const MIN_TITLE_WIDTH = 42; // roughly matches the original fixed 5-column (C:G) box
    let _cumulativeTitleWidth = 0;
    let _titleEndColumn = 3;
    for (let col = 3; col <= _tableLastColumn; col++) {
        _cumulativeTitleWidth += _worksheet.getColumn(col).width ?? DEFAULT_COLUMN_WIDTH;
        _titleEndColumn = col;
        if (_cumulativeTitleWidth >= MIN_TITLE_WIDTH) break;
    }
    _titleEndColumn = Math.min(_titleEndColumn, _tableLastColumn - 1);

    generateReportHeaderExcel(
        _worksheet,
        data.vessel.vesselName,
        _title,
        data.selectedCharterers,
        logoImageId,
        _tableLastColumn - 1, // endColumn (0-indexed)
        _titleEndColumn - 1 // titleEndColumn (0-indexed)
    );

    let currentRow = DATA_START_ROW;

    const _headerRow = _worksheet.getRow(currentRow);
    const _subHeaderRow = _worksheet.getRow(currentRow + 1);

    _headerRow.height = 20;
    _subHeaderRow.height = 20;
    let _columnIndex = 1;

    // First header group - tank information
    const tankHeaderCell = _headerRow.getCell(_columnIndex);
    tankHeaderCell.value = 'Tank';
    tankHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    const coatingHeaderCell = _headerRow.getCell(_columnIndex);
    coatingHeaderCell.value = 'Coating';
    coatingHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    const volumeHeaderCell = _headerRow.getCell(_columnIndex);
    volumeHeaderCell.value = '98% Vol.';
    volumeHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    // Apply border after base columns
    for (let i = 1; i <= _columnIndex - 1; i++) {
        setHeaderStyle(_headerRow.getCell(i));
        setHeaderStyle(_subHeaderRow.getCell(i));
    }

    // Add right border to the last base column
    _headerRow.getCell(_columnIndex - 1).border = {
        ..._headerRow.getCell(_columnIndex - 1).border,
        right: { style: 'medium' },
    };
    _subHeaderRow.getCell(_columnIndex - 1).border = {
        ..._subHeaderRow.getCell(_columnIndex - 1).border,
        right: { style: 'medium' },
    };

    // Add cargo column groups - each cargo has 1-3 columns depending on includeUnNo and showFillRatio
    // (Trade Name always; UN No. and/or Fill ratio added when applicable)
    for (let i = 0; i < numberOfSteps; i++) {
        const _cargoHeader = i === 0 ? 'Previous Cargo' : `${i + 1}${getOrdinalSuffix(i + 1)} Previous Cargo`;
        const _lastColumnOffset = _columnsPerCargo - 1;

        // Merge cargo header cells
        _worksheet.mergeCells(currentRow, _columnIndex, currentRow, _columnIndex + _lastColumnOffset);
        _headerRow.getCell(_columnIndex).value = _cargoHeader;

        // Set subheaders
        _subHeaderRow.getCell(_columnIndex).value = cargoNameType === 'tradeName' ? 'Trade Name' : 'Product Name';
        if (includeUnNo) {
            _subHeaderRow.getCell(_columnIndex + 1).value = 'UN No.';
        }
        if (showFillRatio) {
            _subHeaderRow.getCell(_columnIndex + _lastColumnOffset).value = 'Fill ratio';
        }

        // Style cargo headers
        for (let j = 0; j < _columnsPerCargo; j++) {
            setHeaderStyle(_headerRow.getCell(_columnIndex + j));
            setHeaderStyle(_subHeaderRow.getCell(_columnIndex + j));
        }

        // Add right border to the last column in this cargo group
        _headerRow.getCell(_columnIndex + _lastColumnOffset).border = {
            ..._headerRow.getCell(_columnIndex + _lastColumnOffset).border,
            right: { style: 'medium' },
        };
        _subHeaderRow.getCell(_columnIndex + _lastColumnOffset).border = {
            ..._subHeaderRow.getCell(_columnIndex + _lastColumnOffset).border,
            right: { style: 'medium' },
        };

        _columnIndex += _columnsPerCargo;
    }

    // Move to data rows (after header and subheader)
    currentRow += 2;

    // Determines whether a tank's previous cargo history keeps it in the report, per fosfaMode.
    const passesFosfaFilter = (tank: (typeof data.vessel.cargoTanks)[number]): boolean => {
        const _cargoContents = data.cargoContentsPerTank.find((_cargoContent) => _cargoContent.tankId === tank.tankId);
        const _cargoes = _cargoContents?.cargoContent.slice(0, MAX_FOSFA_CARGOES) ?? [];
        if (_cargoes.length === 0) {
            return false;
        }

        const fillRatios = _cargoes.map((_cargo) => {
            const weight = _cargo.weight instanceof Decimal ? _cargo.weight : new Decimal(_cargo.weight ?? 0);
            return weight.gt(0) ? getFormattedFillRatio(weight, data.stowagePlan, _cargo, tank) : null;
        });

        return fosfaMode === 'nobl'
            ? getCargoFosfaCellStatus(_cargoes[0], fillRatios[0]) !== 'banned'
            : getFosfaAcceptedCargoIndex(_cargoes, fillRatios) >= 0;
    };

    let tanks = data.selectedTankIds
        ? data.vessel.cargoTanks.filter((tank) => data.selectedTankIds?.includes(tank.tankId))
        : data.vessel.cargoTanks;

    // By default, when generating an 'approved' or 'nobl' report, only include tanks that pass the
    // mode's filter. Selecting "Include all tanks" bypasses this.
    if (fosfaMode !== 'off' && !includeAllTanks) {
        tanks = tanks.filter((tank) => passesFosfaFilter(tank));
    }

    const { compareTankNamesForLastCargoes } = sortUtils();

    // Add tank data rows
    for (const _tank of tanks.sort((a, b) => compareTankNamesForLastCargoes(a.tankName, b.tankName))) {
        const _dataRow = _worksheet.getRow(currentRow);
        _dataRow.height = 16;

        const coatingName = getCoatingName(_tank.coatingType);

        _dataRow.getCell(1).value = _tank.tankName;
        _dataRow.getCell(2).value = coatingName ?? '';
        _dataRow.getCell(3).value = _tank.volumeAt98Percent;

        // Add right border to the last base column
        _dataRow.getCell(3).border = {
            ..._dataRow.getCell(3).border,
            right: { style: 'medium' },
        };

        // set value format of cell 3 to number
        _dataRow.getCell(3).numFmt = '#,##0.00';

        const _cargoContents = data.cargoContentsPerTank.find((_cargoContent) => _cargoContent.tankId === _tank.tankId);
        const fosfaCargoes = _cargoContents?.cargoContent.slice(0, MAX_FOSFA_CARGOES) ?? [];
        const fosfaFillRatios = fosfaCargoes.map((cargoContent) => {
            const cargoWeight = cargoContent.weight instanceof Decimal ? cargoContent.weight : new Decimal(cargoContent.weight ?? 0);
            return cargoWeight.gt(0) ? getFormattedFillRatio(cargoWeight, data.stowagePlan, cargoContent, _tank) : null;
        });
        const acceptedCargoIndex = getFosfaAcceptedCargoIndex(fosfaCargoes, fosfaFillRatios);

        // Add cargo data
        let _colIndex = 4;
        for (let i = 0; i < numberOfSteps; i++) {
            const _cargo = _cargoContents?.cargoContent[i];
            const _fillRatioColIndex = _colIndex + _columnsPerCargo - 1;

            if (_cargo) {
                const weight = _cargo.weight instanceof Decimal ? _cargo.weight : new Decimal(_cargo.weight ?? 0);
                const fillRatio = weight.gt(0) ? getFormattedFillRatio(weight, data.stowagePlan, _cargo, _tank) : null;

                _dataRow.getCell(_colIndex).value =
                    cargoNameType === 'tradeName' ? _cargo.cargo?.product?.tradeName : _cargo.cargo?.product?.category?.name;
                if (includeUnNo) {
                    _dataRow.getCell(_colIndex + 1).value = _cargo.cargo.product.unCode; // Product UN No.
                }
                if (showFillRatio) {
                    _dataRow.getCell(_fillRatioColIndex).value = fillRatio !== null ? fillRatio : '';
                    _dataRow.getCell(_fillRatioColIndex).numFmt = '0.00%';
                }

                if (fosfaMode === 'approved') {
                    if (i === acceptedCargoIndex) {
                        setCargoColor(_dataRow.getCell(_colIndex), 'C6EFCE');
                    }
                } else if (fosfaMode === 'nobl') {
                    const fosfaStatus = getCargoFosfaCellStatus(_cargo, fillRatio);
                    if (i === 0 && fosfaStatus === 'banned') {
                        setCargoColor(_dataRow.getCell(_colIndex), 'FFC7CE');
                    } else if ((i === 1 || i === 2) && fosfaStatus === 'banned') {
                        setCargoColor(_dataRow.getCell(_colIndex), 'FFC7CE');
                    }
                }
            } else {
                _dataRow.getCell(_colIndex).value = '';
                if (includeUnNo) {
                    _dataRow.getCell(_colIndex + 1).value = '';
                }
                if (showFillRatio) {
                    _dataRow.getCell(_fillRatioColIndex).value = '';
                }
            }

            // Add right border to the last column in each cargo group
            _dataRow.getCell(_fillRatioColIndex).border = {
                ..._dataRow.getCell(_fillRatioColIndex).border,
                right: { style: 'medium' },
            };

            _colIndex += _columnsPerCargo;
        }

        currentRow++;
    }

    // Add borders to table
    const _lastRow = currentRow - 1;
    for (let i = DATA_START_ROW; i <= _lastRow; i++) {
        for (let j = 0; j < _columnIndex - 1; j++) {
            _worksheet.getCell(`${getLetterForNumber(j)}${i}`).border = {
                ..._worksheet.getCell(`${getLetterForNumber(j)}${i}`).border,
                top: { style: 'thin' },
                bottom: { style: 'thin' },
            };
        }
    }

    // Add bottom border to the last row
    for (let j = 0; j < _columnIndex - 1; j++) {
        _worksheet.getCell(`${getLetterForNumber(j)}${_lastRow}`).border = {
            ..._worksheet.getCell(`${getLetterForNumber(j)}${_lastRow}`).border,
            bottom: { style: 'medium' },
        };
    }

    _worksheet.views = [
        { state: 'frozen', xSplit: 0, ySplit: DATA_START_ROW + 1, topLeftCell: `A${DATA_START_ROW + 2}`, activeCell: `A${DATA_START_ROW + 2}` },
    ];
}
